// src/profilepanel.js

import React, { useState, useMemo } from "react";

// ===== PROFILE DATA MODEL =====
export const defaultProfile = {
  basics: {
    name: "",
    age: "",
    sex: "",
    height: "",
    weight: "",
    experience: "",
  },
  measurements: {
    chest: "",
    waist: "",
    hips: "",
    arm: "",
    thigh: "",
    calf: "",
    neck: "",
  },
  goals: {
    primaryGoal: "",
    timeline: "",
    heroTarget: "",
    notes: "",
  },
  notes: {
    injuries: "",
    trainingNotes: "",
    nutritionNotes: "",
  },
  // NEW: measurement history snapshots (every ~2 weeks)
  // Each entry: { id, date, weight, measurements: { chest, waist, ... } }
  measurementHistory: [],
};

const ProfilePanel = ({ profile, setProfile, photos, setPhotos }) => {
  const [section, setSection] = useState("basics");

  // --- derived summary for header ---
  const summary = useMemo(() => {
    const name = profile?.basics?.name?.trim() || "Hero in Training";
    const age = profile?.basics?.age?.trim();
    const height = profile?.basics?.height?.trim();
    const weight = profile?.basics?.weight?.trim();
    const exp = profile?.basics?.experience || "";
    const primaryGoal = profile?.goals?.primaryGoal?.trim();
    const heroTarget = profile?.goals?.heroTarget?.trim();

    let expLabel = "";
    if (exp === "beginner") expLabel = "Beginner";
    else if (exp === "intermediate") expLabel = "Intermediate";
    else if (exp === "advanced") expLabel = "Advanced";

    return {
      name,
      subtitle:
        heroTarget || primaryGoal || "Dial in your stats, then go to war.",
      badgeLeft: age ? `${age} yrs` : null,
      badgeMid:
        height && weight ? `${height} • ${weight}` : height || weight || null,
      badgeRight: expLabel || "Experience: n/a",
    };
  }, [profile]);

  // --- update helpers ---
  const handleBasicsChange = (field, value) => {
    setProfile({
      ...profile,
      basics: {
        ...profile.basics,
        [field]: value,
      },
    });
  };

  const handleMeasChange = (field, value) => {
    setProfile({
      ...profile,
      measurements: {
        ...profile.measurements,
        [field]: value,
      },
    });
  };

  const handleGoalsChange = (field, value) => {
    setProfile({
      ...profile,
      goals: {
        ...profile.goals,
        [field]: value,
      },
    });
  };

  const handleNotesChange = (field, value) => {
    setProfile({
      ...profile,
      notes: {
        ...profile.notes,
        [field]: value,
      },
    });
  };

  // --- measurement history helpers ---
  const measurementHistory = Array.isArray(profile.measurementHistory)
    ? profile.measurementHistory
    : [];

  const formatDelta = (prevVal, currentVal) => {
    if (!prevVal || !currentVal) return "";
    const a = parseFloat(prevVal);
    const b = parseFloat(currentVal);
    if (Number.isNaN(a) || Number.isNaN(b)) return "";
    const diff = b - a;
    if (Math.abs(diff) < 0.01) return " (no change)";
    const sign = diff > 0 ? "+" : "";
    return ` (${sign}${diff.toFixed(1)})`;
  };

  const handleSaveMeasurementCheckpoint = () => {
    const meas = profile.measurements || {};
    const basics = profile.basics || {};
    const hasAnyMeasurement = Object.values(meas).some(
      (v) => v && String(v).trim() !== ""
    );
    const hasWeight = basics.weight && String(basics.weight).trim() !== "";

    if (!hasAnyMeasurement && !hasWeight) {
      window.alert(
        "Add at least your weight or one body measurement before saving a checkpoint."
      );
      return;
    }

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);

    const history = Array.isArray(profile.measurementHistory)
      ? [...profile.measurementHistory]
      : [];

    const entry = {
      id: now.getTime(),
      date: dateStr,
      weight: basics.weight || "",
      measurements: { ...meas },
    };

    // If there's already a checkpoint for today, overwrite it
    if (history.length && history[history.length - 1].date === dateStr) {
      history[history.length - 1] = {
        ...history[history.length - 1],
        ...entry,
      };
    } else {
      history.push(entry);
    }

    setProfile({
      ...profile,
      measurementHistory: history,
    });

    window.alert("Checkpoint saved. Your hero arc has a new data point. 📊");
  };

  // --- photos ---
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);

    const newEntries = files.map((file) => ({
      id: `${now.getTime()}-${file.name}-${Math.random()}`,
      fileName: file.name,
      url: URL.createObjectURL(file),
      date: dateStr,
      note: "",
    }));

    setPhotos([...photos, ...newEntries]);
    e.target.value = "";
  };

  const handlePhotoNoteChange = (id, value) => {
    setPhotos(
      photos.map((p) =>
        p.id === id
          ? {
              ...p,
              note: value,
            }
          : p
      )
    );
  };

  const handleDeletePhoto = (id) => {
    const toDelete = photos.find((p) => p.id === id);
    if (toDelete && toDelete.url) {
      URL.revokeObjectURL(toDelete.url);
    }
    setPhotos(photos.filter((p) => p.id !== id));
  };

  // --- section renderers ---
  const renderBasics = () => (
    <div>
      <p className="program-desc">
        This is your <strong>hero origin card</strong>. Keep it up to date so
        you can see exactly how your training is changing your body over time.
      </p>

      <div className="profile-field-group">
        <div className="profile-field-row">
          <div className="profile-field">
            <label>Preferred Name</label>
            <input
              value={profile.basics.name}
              onChange={(e) => handleBasicsChange("name", e.target.value)}
              placeholder="e.g. Kyle"
            />
          </div>
          <div className="profile-field">
            <label>Age</label>
            <input
              value={profile.basics.age}
              onChange={(e) => handleBasicsChange("age", e.target.value)}
              placeholder="29"
              inputMode="numeric"
            />
          </div>
        </div>

        <div className="profile-field-row">
          <div className="profile-field">
            <label>Sex</label>
            <select
              value={profile.basics.sex}
              onChange={(e) => handleBasicsChange("sex", e.target.value)}
            >
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other / Prefer not to say</option>
            </select>
          </div>
          <div className="profile-field">
            <label>Training Experience</label>
            <select
              value={profile.basics.experience}
              onChange={(e) => handleBasicsChange("experience", e.target.value)}
            >
              <option value="">Select</option>
              <option value="beginner">Beginner (0–1 yr)</option>
              <option value="intermediate">Intermediate (1–3 yrs)</option>
              <option value="advanced">Advanced (3+ yrs)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="profile-field-group">
        <div className="section-label" style={{ marginBottom: 6 }}>
          Current body stats
        </div>
        <div className="profile-field-row">
          <div className="profile-field">
            <label>Height</label>
            <input
              value={profile.basics.height}
              onChange={(e) => handleBasicsChange("height", e.target.value)}
              placeholder={`e.g. 5'9" or 175 cm`}
            />
          </div>
          <div className="profile-field">
            <label>Bodyweight</label>
            <input
              value={profile.basics.weight}
              onChange={(e) => handleBasicsChange("weight", e.target.value)}
              placeholder="e.g. 180 lbs / 82 kg"
            />
          </div>
        </div>
        <div className="small-muted">
          Tip: update these every 2–4 weeks to see how your training phase is
          actually moving the needle.
        </div>
      </div>
    </div>
  );

  const renderMeasurements = () => {
    const historyDesc = [...measurementHistory].reverse(); // newest first
    const measurementLabels = {
      chest: "Chest",
      waist: "Waist",
      hips: "Hips",
      arm: "Arm",
      thigh: "Thigh",
      calf: "Calf",
      neck: "Neck",
    };

    return (
      <div>
        <p className="program-desc">
          These are your <strong>hard numbers</strong>. Measure in the morning,
          after the bathroom, before eating, in similar conditions each time.
        </p>

        <div className="profile-field-group">
          <div className="section-label" style={{ marginBottom: 6 }}>
            Torso
          </div>
          <div className="profile-field-row">
            <div className="profile-field">
              <label>Chest</label>
              <input
                value={profile.measurements.chest}
                onChange={(e) => handleMeasChange("chest", e.target.value)}
                placeholder="inches or cm"
              />
            </div>
            <div className="profile-field">
              <label>Waist</label>
              <input
                value={profile.measurements.waist}
                onChange={(e) => handleMeasChange("waist", e.target.value)}
                placeholder="inches or cm"
              />
            </div>
            <div className="profile-field">
              <label>Hips</label>
              <input
                value={profile.measurements.hips}
                onChange={(e) => handleMeasChange("hips", e.target.value)}
                placeholder="inches or cm"
              />
            </div>
          </div>
        </div>

        <div className="profile-field-group">
          <div className="section-label" style={{ marginBottom: 6 }}>
            Limbs
          </div>
          <div className="profile-field-row">
            <div className="profile-field">
              <label>Arm (flexed)</label>
              <input
                value={profile.measurements.arm}
                onChange={(e) => handleMeasChange("arm", e.target.value)}
                placeholder="e.g. 16 in"
              />
            </div>
            <div className="profile-field">
              <label>Thigh</label>
              <input
                value={profile.measurements.thigh}
                onChange={(e) => handleMeasChange("thigh", e.target.value)}
                placeholder="e.g. 24 in"
              />
            </div>
            <div className="profile-field">
              <label>Calf</label>
              <input
                value={profile.measurements.calf}
                onChange={(e) => handleMeasChange("calf", e.target.value)}
                placeholder="e.g. 16 in"
              />
            </div>
          </div>
          <div className="profile-field-row">
            <div className="profile-field">
              <label>Neck</label>
              <input
                value={profile.measurements.neck}
                onChange={(e) => handleMeasChange("neck", e.target.value)}
                placeholder="optional"
              />
            </div>
          </div>
        </div>

        <div className="small-muted" style={{ marginBottom: 12 }}>
          You don’t need to obsess over tiny changes. Look for trends over
          several weeks, not day-to-day noise.
        </div>

        {/* NEW: Measurement history / checkpoints */}
        <div className="profile-field-group">
          <div className="section-label" style={{ marginBottom: 6 }}>
            Measurement checkpoints (every ~2 weeks)
          </div>
          <button
            onClick={handleSaveMeasurementCheckpoint}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid rgba(56,189,248,0.7)",
              background: "rgba(15,23,42,0.9)",
              color: "#e0f2fe",
              fontSize: "0.85rem",
              cursor: "pointer",
              marginBottom: 6,
            }}
          >
            Save current stats as checkpoint ✅
          </button>
          {measurementHistory.length > 0 && (
            <div className="small-muted">
              Last checkpoint:{" "}
              {measurementHistory[measurementHistory.length - 1].date}
            </div>
          )}
        </div>

        {historyDesc.length === 0 ? (
          <p className="program-desc">
            No checkpoints yet. Take a <strong>Week 0 baseline</strong> before
            your program, then save a new checkpoint about every 2 weeks so you
            can see the arc of your hero build.
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginTop: 8,
            }}
          >
            {historyDesc.map((entry, idx) => {
              const prev = historyDesc[idx + 1] || null;
              return (
                <div
                  key={entry.id}
                  style={{
                    borderRadius: 12,
                    padding: 8,
                    border: "1px solid rgba(148,163,184,0.4)",
                    background: "rgba(15,23,42,0.7)",
                    fontSize: "0.8rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <div>
                      <strong>Checkpoint {historyDesc.length - idx}</strong>
                      <span style={{ opacity: 0.8 }}> • {entry.date}</span>
                    </div>
                    {prev && (
                      <span style={{ opacity: 0.8 }}>vs. {prev.date}</span>
                    )}
                  </div>

                  {entry.weight && (
                    <div style={{ marginBottom: 2 }}>
                      <strong>Weight:</strong>{" "}
                      {prev && prev.weight
                        ? `${prev.weight} → ${entry.weight}${formatDelta(
                            prev.weight,
                            entry.weight
                          )}`
                        : entry.weight}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 2,
                    }}
                  >
                    {Object.keys(measurementLabels).map((key) => {
                      const currentVal =
                        entry.measurements && entry.measurements[key];
                      const prevVal =
                        prev && prev.measurements && prev.measurements[key];
                      if (!currentVal && !prevVal) return null;
                      return (
                        <div key={key}>
                          <strong>{measurementLabels[key]}:</strong>{" "}
                          {prev && prevVal
                            ? `${prevVal} → ${currentVal || "—"}${formatDelta(
                                prevVal,
                                currentVal
                              )}`
                            : currentVal || "—"}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderGoals = () => (
    <div>
      <p className="program-desc">
        This is where you define <strong>what “win” looks like</strong> for this
        training arc. Clear targets make it easier to know if the plan is
        working.
      </p>

      <div className="profile-field-group">
        <div className="profile-field-row">
          <div className="profile-field">
            <label>Primary Goal</label>
            <input
              value={profile.goals.primaryGoal}
              onChange={(e) => handleGoalsChange("primaryGoal", e.target.value)}
              placeholder="e.g. lean bulk, cut, strength PRs, hero cosplay"
            />
          </div>
          <div className="profile-field">
            <label>Timeline</label>
            <input
              value={profile.goals.timeline}
              onChange={(e) => handleGoalsChange("timeline", e.target.value)}
              placeholder="e.g. 9 weeks, 6 months"
            />
          </div>
        </div>
      </div>

      <div className="profile-field-group">
        <div className="profile-field-row">
          <div className="profile-field">
            <label>Hero Target</label>
            <input
              value={profile.goals.heroTarget}
              onChange={(e) => handleGoalsChange("heroTarget", e.target.value)}
              placeholder="e.g. Thor shoulders, Spider-Man leanness"
            />
          </div>
        </div>
      </div>

      <div className="profile-field-group">
        <div className="profile-field">
          <label>Goal Notes / Criteria</label>
          <textarea
            rows={4}
            value={profile.goals.notes}
            onChange={(e) => handleGoalsChange("notes", e.target.value)}
            placeholder="How will you know you hit this goal? Specific measurements, photos, performance numbers, how you feel, etc."
          />
        </div>
      </div>

      <div className="small-muted">
        The more specific you are here, the easier it is to adjust training and
        nutrition when something isn’t moving.
      </div>
    </div>
  );

  const renderPhotos = () => (
    <div>
      <p className="program-desc">
        Progress photos are your <strong>most honest mirror</strong>. Same
        lighting, same poses, same time of day = brutal but accurate feedback.
      </p>

      <div className="profile-field-group">
        <div className="profile-field-row">
          <div className="profile-field">
            <label>Upload Progress Photos</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
            />
            <span className="small-muted">
              Tip: front, side, back shots. Neutral, consistent lighting. Aim
              for weekly or bi-weekly uploads.
            </span>
          </div>
        </div>
      </div>

      {photos.length === 0 ? (
        <p className="program-desc">
          No photos yet. Start with a <strong>Week 0 baseline</strong> before
          you begin your hero program, then repeat from similar distance and
          angle.
        </p>
      ) : (
        <div className="photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="photo-card">
              <img src={p.url} alt={p.fileName} className="photo-img" />
              <div className="photo-meta">
                <div className="photo-file">{p.fileName}</div>
                <div className="photo-date">Taken: {p.date}</div>
                <textarea
                  className="photo-note"
                  rows={2}
                  placeholder="e.g. Week 3 – down 4 lbs, shoulders popping more."
                  value={p.note}
                  onChange={(e) => handlePhotoNoteChange(p.id, e.target.value)}
                />
                <button
                  className="photo-delete"
                  onClick={() => handleDeletePhoto(p.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderNotes = () => (
    <div>
      <p className="program-desc">
        Use this like a <strong>coach’s notebook for yourself</strong>. Capture
        what’s working, what’s not, and what future-you needs to remember.
      </p>

      <div className="profile-field-group">
        <div className="profile-field">
          <label>Injuries / Constraints</label>
          <textarea
            rows={3}
            value={profile.notes.injuries}
            onChange={(e) => handleNotesChange("injuries", e.target.value)}
            placeholder="e.g. left shoulder history, avoid deep dips, no max deadlifts."
          />
        </div>
      </div>

      <div className="profile-field-group">
        <div className="profile-field">
          <label>Training Notes</label>
          <textarea
            rows={3}
            value={profile.notes.trainingNotes}
            onChange={(e) => handleNotesChange("trainingNotes", e.target.value)}
            placeholder="Cues that help, warm-ups that make you feel good, things that wreck recovery, volume that feels ideal, etc."
          />
        </div>
      </div>

      <div className="profile-field-group">
        <div className="profile-field">
          <label>Nutrition Notes</label>
          <textarea
            rows={3}
            value={profile.notes.nutritionNotes}
            onChange={(e) =>
              handleNotesChange("nutritionNotes", e.target.value)
            }
            placeholder="What style of eating actually works for you? Protein targets, carb timing, meal frequency, foods that help or hurt performance."
          />
        </div>
      </div>
    </div>
  );

  // --- main render ---
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">{summary.name}</div>
          <div className="hero-physique" style={{ opacity: 0.85 }}>
            {summary.subtitle}
          </div>
        </div>
        <div className="tag">Profile • Stats • Goals</div>
      </div>

      {/* compact summary chips under header */}
      <div className="profile-summary-strip">
        {summary.badgeLeft && (
          <span className="badge">{summary.badgeLeft}</span>
        )}
        {summary.badgeMid && (
          <span className="badge badge-soft">{summary.badgeMid}</span>
        )}
        <span className="badge badge-outline">{summary.badgeRight}</span>
      </div>

      <div className="profile-layout">
        {/* left: vertical tabs (mobile-first, but stacked nicely) */}
        <div className="profile-sidebar">
          <button
            className={
              section === "basics" ? "profile-tab active" : "profile-tab"
            }
            onClick={() => setSection("basics")}
          >
            <span className="profile-tab-icon">🧬</span>
            <span>Basics</span>
          </button>
          <button
            className={
              section === "measurements" ? "profile-tab active" : "profile-tab"
            }
            onClick={() => setSection("measurements")}
          >
            <span className="profile-tab-icon">📏</span>
            <span>Measurements</span>
          </button>
          <button
            className={
              section === "goals" ? "profile-tab active" : "profile-tab"
            }
            onClick={() => setSection("goals")}
          >
            <span className="profile-tab-icon">🎯</span>
            <span>Goals</span>
          </button>
          <button
            className={
              section === "photos" ? "profile-tab active" : "profile-tab"
            }
            onClick={() => setSection("photos")}
          >
            <span className="profile-tab-icon">📸</span>
            <span>Progress Photos</span>
          </button>
          <button
            className={
              section === "notes" ? "profile-tab active" : "profile-tab"
            }
            onClick={() => setSection("notes")}
          >
            <span className="profile-tab-icon">📝</span>
            <span>Notes</span>
          </button>
        </div>

        {/* right: section content */}
        <div className="profile-content">
          {section === "basics" && renderBasics()}
          {section === "measurements" && renderMeasurements()}
          {section === "goals" && renderGoals()}
          {section === "photos" && renderPhotos()}
          {section === "notes" && renderNotes()}
        </div>
      </div>

      <div className="footer-note">
        Profile details are stored locally in this browser. Photos are kept in
        this session only and are not uploaded anywhere. This is{" "}
        <strong>your</strong> data, for <strong>your</strong> hero arc.
      </div>
    </div>
  );
};

export default ProfilePanel;
